import AppForms from "@toothfairy/shared-ui/AppForms";
import AppAssets from "@toothfairy/shared-ui/AppAssets";
import AppStateManager from "@toothfairy/shared-ui/AppStateManager";
import AppTheme from "@toothfairy/shared-ui/AppTheme";
import React, { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { View } from "react-native";
import ToothFairyButton from "../../../../Components/ToothFairyComponents/ToothFairyButton";
import Constants from "../../../../Constants";
import useWorkspaces from "../../../../Hooks/useWorkspaces";
import useSettingsChipHref from "../../../../Hooks/useSettingsChipHref";
import IdCopyView from "../../../../Components/ToothFairyComponents/IdCopyView";
import { isLargeScreen } from "../../../MainViewContainer";
import Utility from "../../../../Utility";
import Color from "../../../../Utility/Color";
import {
  BuilderSection,
  PassthroughSection,
  useBuilderDirtyTracking,
} from "./BuilderSections";

const PROMPT_SECTION_FIELD_MAP = {
  profile: ["label"],
  content: ["interpolationString", "promptLength"],
  sharing: ["availableToAgents"],
};

const GenerationSettingsAIView = ({
  isEdit,
  settingType,
  handleDeletion,
  selectedId,
  customPrompt,
  handleForking = () => {},
  isBuilderLayout,
  builderActiveSection,
  onBuilderContextChange,
  saveVersion,
}) => {
  const { t } = useTranslation();
  const { currentTheme } = AppTheme.useTheme();
  const entityName = "Prompting";

  const { state } = AppStateManager.useAppStateManager();
  const { getActiveWorkspace } = useWorkspaces();
  const chipHrefRoles = useSettingsChipHref("ROLES_SETTINGS");
  const _title = useCallback(() => {
    return isEdit ? t("common.save") : t("common.create");
  }, [entityName, isEdit]);
  const { setFieldValue, values, initialValues, errors, submitCount } =
    AppForms.useFormikContext();

  const _showBuilderSection = (sectionId) =>
    builderActiveSection == null || builderActiveSection === sectionId;
  const SectionRenderer = isBuilderLayout ? BuilderSection : PassthroughSection;

  useBuilderDirtyTracking({
    isBuilderLayout,
    values,
    initialValues,
    saveVersion,
    onBuilderContextChange,
    errors,
    submitCount,
    sectionFieldMap: PROMPT_SECTION_FIELD_MAP,
  });
  const colourStyles = {
    control: (styles) => ({
      ...styles,
      backgroundColor: currentTheme?.theme?.inputBackgroundColor,
      borderColor: currentTheme?.theme?.border_color,
      fontFamily: "normalFont",
      fontSize: currentTheme?.theme?.small_font_size,
      borderRadius: 10,
    }),
    option: (styles, { data, isDisabled, isFocused, isSelected }) => {
      let color = new Color(data?.backgroundColor || currentTheme?.theme?.grey);
      return {
        ...styles,
        backgroundColor: isDisabled
          ? undefined
          : isSelected
          ? color
          : isFocused
          ? color.alpha(0.1).css()
          : undefined,
        color: isDisabled
          ? "#ccc"
          : isSelected
          ? currentTheme?.theme?.white
            ? "white"
            : "black"
          : data?.backgroundColor || currentTheme?.theme?.font_color,
        cursor: isDisabled ? "not-allowed" : "default",

        ":active": {
          ...styles[":active"],
          backgroundColor: !isDisabled
            ? isSelected
              ? data?.backgroundColor
              : color.alpha(0.3).css()
            : undefined,
        },
      };
    },
    multiValue: (styles, { data }) => {
      let color = new Color(currentTheme?.theme?.primary);
      return {
        ...styles,
        backgroundColor: currentTheme?.theme?.primary_medium,
      };
    },
    multiValueLabel: (styles, { data }) => ({
      ...styles,
      color: data?.backgroundColor || currentTheme?.theme?.font_color,
    }),
    multiValueRemove: (styles, { data }) => ({
      ...styles,
      color: currentTheme?.theme?.white,
      ":hover": {
        backgroundColor: data?.backgroundColor,
        color: "white",
      },
    }),
    menu: (styles) => ({
      ...styles,
      backgroundColor: currentTheme?.theme?.inputBackgroundColor,
      borderColor: currentTheme?.theme?.lightGray,
      fontFamily: "normalFont",
      fontSize: currentTheme?.theme?.small_font_size,
    }),
  };
  return (
    <>
      {_showBuilderSection("profile") && (
        <SectionRenderer
          title={t("prompts.builderSectionProfile", {
            defaultValue: "Overview",
          })}
          icon={AppAssets.icons.persona}
        >
          <View>
            {isEdit && (
              <IdCopyView
                id={selectedId}
                label="ID"
                wrapperStyle={{
                  marginBottom: 10,
                }}
              />
            )}

            <AppForms.AppFormField
              showTextInputLabel
              isMandatory
              label={t("common.name")}
              placeholder={t("settings.generation.promptNamePlaceholder")}
              keyboardtype="default"
              textContextType="label"
              autoCorrect={false}
              name="label"
            />
          </View>
        </SectionRenderer>
      )}

      {_showBuilderSection("content") && (
        <SectionRenderer
          title={t("prompts.builderSectionContent", {
            defaultValue: "Prompt",
          })}
          icon={AppAssets.icons.writer}
          maxWidth={750}
        >
          <View>
            <AppForms.AppFormField
              showTextInputLabel
              isMandatory
              wrapperInputStyle={{
                paddingTop: 10,
              }}
              textContainer={{
                minHeight: 490,
                paddingVertical: 10,
                marginHorizontal: 5,
              }}
              multiline={true}
              label={t("settings.generation.promptText")}
              placeholder={t(
                "settings.generation.interpolationPromptPlaceholder"
              )}
              keyboardtype="default"
              textContextType="label"
              autoCorrect={false}
              name="interpolationString"
            />
          </View>
        </SectionRenderer>
      )}

      {_showBuilderSection("sharing") && (
        <SectionRenderer
          title={t("prompts.builderSectionSharing", {
            defaultValue: "Availability",
          })}
          icon={AppAssets.icons.taskAgent}
        >
          <AppForms.AppFormMultiSelect
            name="availableToAgents"
            chipHref={chipHrefRoles}
            placeholder={t("settings.generation.assignAgentsPlaceholder")}
            hidePlaceholder={true}
            items={state?.agents}
            uniqueKey="id"
            showDropDowns={true}
            readOnlyHeadings={true}
            labelOption="label"
            groupByProperty="agentType"
            style={{ maxWidth: 800 }}
            wrapperContainerStyle={{
              flex: !isLargeScreen() ? 1 : 0.5,
              zIndex: 20,
              paddingTop: 10,
              marginBottom: 10,
              marginHorizontal: 5,
            }}
            styles={colourStyles}
            options={state?.agents?.map((u) => {
              return {
                ...u,
                label: u?.label,
                value: u?.id,
                backgroundColor: Utility?.mapAgentTypeToColour(u, currentTheme),
              };
            })}
          />
        </SectionRenderer>
      )}
      {!isBuilderLayout && (
        <View style={{ alignItems: "center" }}>
          <AppForms.AppSubmitButton
            title={_title()}
            awesomeIconColor={currentTheme?.theme?.white}
            backgroundColor={currentTheme?.theme?.primary}
            btnContentStyle={{
              paddingLeft: 0,
              paddingRight: 0,
            }}
            style={[
              {
                height: Constants.appButtonHeight,
                width: "auto",
                shadowColor: currentTheme?.theme?.transparent,
                marginHorizontal: 10,
              },
            ]}
            // btnLabelStyle={{ fontSize: 16 }}
            wrapperStyle={[
              {
                width: "fit-content",
                minWidth: 150,
                marginTop: 20,
                height: Constants.appButtonHeight,
              },
            ]}
          />

          {isEdit && (
            <ToothFairyButton
              title={t("common.delete")}
              onPress={handleDeletion}
              isDeletion
              wrapperStyle={{
                marginTop: 30,
                minWidth: 150,
                height: Constants.appButtonHeight,
              }}
            />
          )}
        </View>
      )}
    </>
  );
};

export default GenerationSettingsAIView;
